import type { PlanDetail } from '@/api/gen/_model/planDetail';
import { PlanDetailSourceType } from '@/api/gen/_model/planDetailSourceType';
import { type PlanSourceType, type UserInfo } from '@/types';

/** {@link mapPlanDetail} 가 채우는 플랜 축 필드. 두 호출부가 이 목록을 공유한다. */
export type MappedPlanDetail = Pick<
  UserInfo,
  | 'planType'
  | 'planStatus'
  | 'upcomingPlan'
  | 'planDuration'
  | 'planSourceType'
  | 'selfServicePurchase'
>;

/**
 * 화이트리스트는 첫 호출 때 만든다.
 *
 * 모듈 로드 시점에 `Object.values(PlanDetailSourceType)` 를 실행하면, `@/api` 를
 * 통째로 목하는 테스트가 이 파일을 import 체인으로 끌어오기만 해도 목에 없는
 * export 를 건드려 스위트 전체가 로드 단계에서 죽는다. 실제로 이 매퍼가
 * `UserContext` 를 통해 딸려 오는 테스트들이 그렇게 깨졌다.
 */
let planSourceTypes: Set<string> | undefined;
const getPlanSourceTypes = () =>
  (planSourceTypes ??= new Set<string>(Object.values(PlanDetailSourceType)));

/**
 * 아는 `sourceType` 만 통과시킨다.
 *
 * 타입상으로는 이미 좁혀져 있지만, 서버가 나중에 새 발급 경로를 추가했을 때
 * 그 값이 그대로 흘러 들어오면 "직접 구매가 아님"을 놓친다. 모르는 값은
 * `undefined`(= 모름)로 떨어뜨려, 업셀링이 넘겨짚는 대신 안전한 화면을 고르게 한다.
 */
const readPlanSourceType = (plan: PlanDetail): PlanSourceType | undefined =>
  plan.sourceType && getPlanSourceTypes().has(plan.sourceType)
    ? plan.sourceType
    : undefined;

/**
 * `PlanDetail` 을 앱의 `UserInfo` 플랜 필드로 옮기는 **단일 진실원**.
 *
 * `GET /auth/profile` 과 `GET /quiz-plans/options` 는 같은 `PlanDetail` 을 내려주는데,
 * 예전에는 두 호출부(`buildUserInfo` · `transformCurrentPlanOptions`)가 각자 인라인으로
 * 매핑했다. 그 구조에는 두 가지 함정이 있었다.
 *
 * 1. 기본값이 어긋나면 두 응답이 `user` 를 번갈아 덮어써 리렌더 핑퐁이 난다(React #185).
 * 2. 한쪽에만 필드를 추가하면 — `transformCurrentPlanOptions` 의 반환 타입이 명시적
 *    `Pick` 이라 타입 검사에 걸리지도 않는다 — 그 값은 상시 갱신되는 options 응답이
 *    도착해도 영영 바뀌지 않는다. 부팅 때 한 번 채워진 뒤 조용히 stale 해진다.
 *
 * 매핑을 여기 하나로 모아 두 경로가 구조적으로 어긋날 수 없게 한다. 필드를 추가할 때도
 * 이 함수만 고치면 양쪽에 동시에 반영된다.
 */
export const mapPlanDetail = (plan: PlanDetail): MappedPlanDetail => ({
  planType: plan.planType || 'FREE',
  planStatus: plan.planStatus,
  upcomingPlan: plan.upcomingPlan ?? null,
  planDuration: plan.startDate
    ? { startDate: plan.startDate || '', endDate: plan.endDate || '' }
    : { startDate: '', endDate: '' },
  planSourceType: readPlanSourceType(plan),
  selfServicePurchase: plan.selfServicePurchase,
});
